/* eslint-disable radix */
/* eslint-disable camelcase */
import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Text,
  Flex,
  Button,
  Stack,
  useBreakpointValue,
  Textarea,
  useToast,
  SimpleGrid,
  Badge,
  Alert,
  AlertIcon
} from '@chakra-ui/react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FiClock, FiCalendar, FiRepeat, FiInfo } from 'react-icons/fi'

import { CardContainer } from '../../components/molecules/CardContainer'
import { Input } from '../../components/Form/Input'
import { TextFieldControlled } from '../../components/Form/TextFieldControlled'
import { LayoutDefault } from '../../layouts/Default'
import { Switch } from '../../components/Form/Switch'
import { useService } from './service'
import { remaskCaractersAll } from '../../utils/fns/removeCaracters'
import { SelectComponent } from '../../components/Form/SelectComponent'
import { Select } from '../../components/Form/Select'
import { useAuth } from '../../hooks/auth'
import { formatToDateTimeInput, toUtcFromClinic } from '../../utils/timezone'

type FormData = {
  description: string
  repeat: boolean
}

type SelectType = {
  label: string
  value: number
}

type SelectTypeWeek = {
  label: string
  value: string
}

type BlockMode = 'time' | 'days' | 'range'

const MAX_RANGE_DAYS = 186

// Returns today's date in YYYY-MM-DD format
const getTodayStr = () => {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Returns today's datetime in YYYY-MM-DDTHH:mm format (for datetime-local min)
const getTodayDateTimeStr = () => {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  const hh = String(today.getHours()).padStart(2, '0')
  const mm = String(today.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

// Returns a date 6 months from today in YYYY-MM-DD format
const getMaxDateStr = () => {
  const max = new Date()
  max.setMonth(max.getMonth() + 6)
  const y = max.getFullYear()
  const m = String(max.getMonth() + 1).padStart(2, '0')
  const d = String(max.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Returns a datetime 6 months from today in YYYY-MM-DDTHH:mm format
const getMaxDateTimeStr = () => {
  const max = new Date()
  max.setMonth(max.getMonth() + 6)
  const y = max.getFullYear()
  const m = String(max.getMonth() + 1).padStart(2, '0')
  const d = String(max.getDate()).padStart(2, '0')
  const hh = String(max.getHours()).padStart(2, '0')
  const mm = String(max.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

export function CreateOrUpdateBlockScheduling() {
  const [t] = useTranslation('pageBlockTimes')
  const { decodeToken } = useAuth()
  const toast = useToast()
  const service = useService()
  const history = useHistory()
  const { state }: any = history.location

  const { register, handleSubmit, formState } = useForm<FormData>({
    defaultValues: {
      repeat: Boolean(state?.expand_recurrent_days || state?.is_recurrent)
    }
  })

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true
  })

  const clinicTimezone = decodeToken?.clinic?.timezone || 'America/Sao_Paulo'
  const localMethod =
    history.location.pathname.indexOf('create') !== -1 ? 'create' : 'update'

  const todayStr = useMemo(() => getTodayStr(), [])
  const todayDateTimeStr = useMemo(() => getTodayDateTimeStr(), [])
  const maxDateStr = useMemo(() => getMaxDateStr(), [])
  const maxDateTimeStr = useMemo(() => getMaxDateTimeStr(), [])

  const [name, setName] = useState('')
  const [specialist, setSpecialist] = useState<SelectType[]>([])
  const [weeksDay, setWeeksDay] = useState<SelectTypeWeek[]>([])

  const [startTime, setStartTime] = useState('')
  const [finishTime, setFinishTime] = useState('')

  const [initHour, setInitHour] = useState('')
  const [finishHour, setFinishHour] = useState('')

  const [rangeStartDate, setRangeStartDate] = useState('')
  const [rangeFinishDate, setRangeFinishDate] = useState('')
  const [rangeStartHour, setRangeStartHour] = useState('')
  const [rangeFinishHour, setRangeFinishHour] = useState('')

  const [selectedMode, setSelectedMode] = useState<BlockMode>('time')

  const optionsMode = useMemo(
    () => [
      { value: 'time', label: `${t('options.1')}` },
      { value: 'days', label: `${t('options.2')}` },
      { value: 'range', label: 'Criar vários períodos' }
    ],
    [t]
  )

  const optionsDays = useMemo(
    () => [
      { value: '1', label: `${t('optionsDays.1')}` },
      { value: '2', label: `${t('optionsDays.2')}` },
      { value: '3', label: `${t('optionsDays.3')}` },
      { value: '4', label: `${t('optionsDays.4')}` },
      { value: '5', label: `${t('optionsDays.5')}` },
      { value: '6', label: `${t('optionsDays.6')}` },
      { value: '7', label: `${t('optionsDays.7')}` }
    ],
    [t]
  )

  const availableOptionsMode = useMemo(() => {
    if (localMethod === 'update') {
      return optionsMode.filter((option) => option.value !== 'range')
    }
    return optionsMode
  }, [localMethod, optionsMode])

  const splitDateTime = (value: string) => {
    const normalized = value.trim().replace(' ', 'T')
    const [date, hour] = normalized.split('T')
    return { date, hour }
  }

  const getDiffInDays = (startDate: string, finishDate: string) => {
    const start = new Date(`${startDate}T00:00:00`)
    const finish = new Date(`${finishDate}T00:00:00`)
    const diffMs = finish.getTime() - start.getTime()
    return Math.floor(diffMs / (1000 * 60 * 60 * 24))
  }

  const rangePreview = useMemo(() => {
    if (
      selectedMode !== 'range' ||
      !rangeStartDate ||
      !rangeFinishDate ||
      weeksDay.length === 0 ||
      rangeStartDate > rangeFinishDate
    ) {
      return 0
    }

    const selectedDays = new Set(weeksDay.map((day) => Number(day.value)))
    const start = new Date(`${rangeStartDate}T00:00:00`)
    const finish = new Date(`${rangeFinishDate}T00:00:00`)
    const current = new Date(start)
    let total = 0

    while (current <= finish) {
      const jsDay = current.getDay()
      const isoDay = jsDay === 0 ? 7 : jsDay
      if (selectedDays.has(isoDay)) {
        total += 1
      }
      current.setDate(current.getDate() + 1)
    }

    return total
  }, [rangeFinishDate, rangeStartDate, selectedMode, weeksDay])

  useEffect(() => {
    if (localMethod !== 'update' || !state) return

    const isRecurrentBlock = Boolean(state?.week_day || state?.is_recurrent)
    setSelectedMode(isRecurrentBlock ? 'days' : 'time')
    setName(state?.name || '')

    if (state?.start_time) {
      setStartTime(formatToDateTimeInput(state.start_time, clinicTimezone))
    }
    if (state?.finish_time) {
      setFinishTime(formatToDateTimeInput(state.finish_time, clinicTimezone))
    }
    if (state?.recurrent_start_time) {
      setInitHour(state.recurrent_start_time.slice(0, 5))
    }
    if (state?.recurrent_finish_time) {
      setFinishHour(state.recurrent_finish_time.slice(0, 5))
    }
    if (state?.specialists) {
      const formattedSpecialists = state.specialists.map((user: any) => ({
        value: user?.id,
        label: user?.social_name || user?.name
      }))
      setSpecialist(formattedSpecialists)
    }
    if (state?.week_day) {
      const selectedWeekDays = String(state.week_day)
        .split(',')
        .map((item) => optionsDays.find((week) => week.value === item))
        .filter(Boolean) as SelectTypeWeek[]
      setWeeksDay(selectedWeekDays)
    }
  }, [clinicTimezone, localMethod, optionsDays, state])

  const handleChangeMode = (mode: BlockMode) => {
    setSelectedMode(mode)

    if (mode === 'time') {
      setInitHour('')
      setFinishHour('')
      setWeeksDay([])
      setRangeStartDate('')
      setRangeFinishDate('')
      setRangeStartHour('')
      setRangeFinishHour('')
      return
    }

    if (mode === 'days') {
      setStartTime('')
      setFinishTime('')
      setRangeStartDate('')
      setRangeFinishDate('')
      setRangeStartHour('')
      setRangeFinishHour('')
      return
    }

    setStartTime('')
    setFinishTime('')
    setInitHour('')
    setFinishHour('')
  }

  const validateRangeMode = () => {
    if (rangeStartDate === '' || rangeFinishDate === '') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Informe a data inicial e a data final'
      })
      return false
    }

    if (rangeStartDate > rangeFinishDate) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'A data final precisa ser maior ou igual à data inicial'
      })
      return false
    }

    if (getDiffInDays(rangeStartDate, rangeFinishDate) > MAX_RANGE_DAYS) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'O período máximo permitido é de 6 meses'
      })
      return false
    }

    if (weeksDay.length === 0) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Selecione ao menos um dia da semana'
      })
      return false
    }

    if (rangeStartHour === '' || rangeFinishHour === '') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Informe o horário inicial e final'
      })
      return false
    }

    const initValue = Number(remaskCaractersAll(rangeStartHour))
    const endValue = Number(remaskCaractersAll(rangeFinishHour))

    if (initValue >= endValue) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Revise as horas, horário inválido'
      })
      return false
    }

    return true
  }

  const validateSingleTimeMode = () => {
    if (startTime === '') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Selecione a data hora inicial'
      })
      return false
    }

    if (finishTime === '') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Selecione a data hora final'
      })
      return false
    }

    if (startTime >= finishTime) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Revise as datas, período inválido'
      })
      return false
    }

    return true
  }

  const validateRecurrentDaysMode = () => {
    if (weeksDay.length === 0) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Selecione ao menos um dia da semana'
      })
      return false
    }

    if (initHour === '' || finishHour === '') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Informe o horário inicial e final'
      })
      return false
    }

    const initValue = Number(remaskCaractersAll(initHour))
    const endValue = Number(remaskCaractersAll(finishHour))

    if (initValue >= endValue) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Revise as Horas, horário inválido'
      })
      return false
    }

    return true
  }

  const handleCreateOrUpdateBlock: SubmitHandler<FormData> = async (
    values
  ) => {
    const { description, repeat } = values

    if (specialist?.length === 0) {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Selecione ao menos um especialista'
      })
      return
    }

    if (localMethod === 'update' && selectedMode === 'range') {
      toast({
        duration: 3000,
        status: 'warning',
        position: 'top',
        title: 'Criação em lote está disponível apenas para novos bloqueios'
      })
      return
    }

    if (selectedMode === 'time' && !validateSingleTimeMode()) return
    if (selectedMode === 'days' && !validateRecurrentDaysMode()) return
    if (selectedMode === 'range' && !validateRangeMode()) return

    const specialists = specialist.map((user) => ({ id: user.value }))
    const days = weeksDay.map((day) => day.value)

    if (selectedMode === 'range') {
      const payload = {
        name,
        description: description === '' ? null : description,
        range_start_date: rangeStartDate,
        range_finish_date: rangeFinishDate,
        recurrent_start_time: `${rangeStartHour}:00`,
        recurrent_finish_time: `${rangeFinishHour}:00`,
        specialists,
        week_day: days.length > 0 ? days.join(',') : null,
        expand_recurrent_days: selectedMode === 'range' ? repeat : false
      }

      await service.bulkCreateRequest(payload)
      return
    }

    const payload = {
      name,
      description: description === '' ? null : description,
      start_time:
        selectedMode === 'time' && startTime
          ? toUtcFromClinic(
              splitDateTime(startTime).date,
              splitDateTime(startTime).hour,
              clinicTimezone
            )
          : null,
      finish_time:
        selectedMode === 'time' && finishTime
          ? toUtcFromClinic(
              splitDateTime(finishTime).date,
              splitDateTime(finishTime).hour,
              clinicTimezone
            )
          : null,
      recurrent_start_time:
        selectedMode === 'days' && initHour ? `${initHour}:00` : null,
      recurrent_finish_time:
        selectedMode === 'days' && finishHour ? `${finishHour}:00` : null,
      specialists,
      week_day:
        selectedMode === 'days' && days.length > 0 ? days.join(',') : null,
      expand_recurrent_days: selectedMode === 'days' ? repeat : false,
      is_recurrent: selectedMode === 'days'
    }

    if (localMethod === 'create') {
      await service.createRequest(payload)
    } else {
      await service.updateRequest(payload, state.id)
    }
  }

  // Mode icon & color helpers
  const modeInfo = {
    time: { icon: <FiClock />, color: 'blue', label: 'Período único' },
    days: { icon: <FiRepeat />, color: 'purple', label: 'Dias recorrentes' },
    range: { icon: <FiCalendar />, color: 'orange', label: 'Criação em lote' }
  }

  return (
    <LayoutDefault
      title={
        localMethod === 'create' ? `${t('create.title')}` : `${t('updateTitle')}`
      }
      urlBack="/settings/block-times"
      onSubmit={handleSubmit(handleCreateOrUpdateBlock)}
    >
      <Stack spacing={5} w="100%">
        {/* Card 1 — Identificação */}
        <CardContainer title={t('create.container1.title')}>
          <SimpleGrid columns={[1, 1, 2, 3]} spacing={4} mt={2}>
            <Box>
              <Input
                label={t('create.container1.i1')}
                placeholder={t('create.container1.placeholder.name')}
                isRequired
                value={name}
                name="name"
                maxLength={40}
                onChange={(event) =>
                  setName(event?.target.value.toUpperCase())
                }
              />
            </Box>

            <Box>
              <Select
                name="selectedMode"
                options={availableOptionsMode}
                label="Tipo de bloqueio"
                onChange={(event: any) => {
                  handleChangeMode(event.target.value)
                }}
                value={selectedMode}
              />
            </Box>

            {/* Mode badge indicator */}
            <Flex alignItems="flex-end" pb={1}>
              <Badge
                colorScheme={modeInfo[selectedMode].color}
                px={3}
                py={1}
                borderRadius="full"
                display="flex"
                alignItems="center"
                gap={1}
                fontSize="sm"
              >
                {modeInfo[selectedMode].icon}
                <Box ml={1}>{modeInfo[selectedMode].label}</Box>
              </Badge>
            </Flex>
          </SimpleGrid>
        </CardContainer>

        {/* Card 2 — Configuração do período (modo: time) */}
        {selectedMode === 'time' && (
          <CardContainer title="Período do Bloqueio">
            <Alert status="info" borderRadius="md" mb={4} py={2} fontSize="sm">
              <AlertIcon boxSize={4} />
              Bloqueios permitidos a partir de hoje até 6 meses à frente.
            </Alert>
            <SimpleGrid columns={[1, 1, 2]} spacing={4} mt={2}>
              <Box>
                <Input
                  label={t('create.container1.i2')}
                  name="startTime"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  isRequired
                  type="datetime-local"
                  min={todayDateTimeStr}
                  max={maxDateTimeStr}
                />
              </Box>

              <Box>
                <Input
                  label={t('create.container1.i3')}
                  name="finishTime"
                  value={finishTime}
                  onChange={(event) => setFinishTime(event.target.value)}
                  isRequired
                  type="datetime-local"
                  min={startTime || todayDateTimeStr}
                  max={maxDateTimeStr}
                />
              </Box>
            </SimpleGrid>
          </CardContainer>
        )}

        {/* Card 2 — Configuração do período (modo: days) */}
        {selectedMode === 'days' && (
          <CardContainer title="Configuração Recorrente">
            <SimpleGrid columns={[1, 1, 2, 4]} spacing={4} mt={2}>
              <Box gridColumn={['1', '1', 'span 2']}>
                <SelectComponent
                  options={optionsDays}
                  isMulti
                  name="days"
                  value={weeksDay}
                  onChange={(e: SelectTypeWeek[]) => setWeeksDay(e || [])}
                  placeholder={t('create.container1.placeholder.days')}
                  title={t('options.2')}
                />
              </Box>

              <Box>
                <TextFieldControlled
                  label={t('create.container1.i6')}
                  name="initHour"
                  value={initHour}
                  onChange={(event) => setInitHour(event.target.value)}
                  isRequired
                  type="time"
                />
              </Box>

              <Box>
                <Input
                  label={t('create.container1.i7')}
                  name="finishHour"
                  value={finishHour}
                  onChange={(event) => setFinishHour(event.target.value)}
                  isRequired
                  type="time"
                />
              </Box>
            </SimpleGrid>

            <Flex mt={4} alignItems="center" gap={3}>
              <Switch
                {...register('repeat')}
                defaultChecked={Boolean(
                  state?.expand_recurrent_days || state?.is_recurrent
                )}
                label={t('repeat')}
              />
            </Flex>
          </CardContainer>
        )}

        {/* Card 2 — Configuração do período (modo: range) */}
        {selectedMode === 'range' && (
          <CardContainer title="Criação em Lote">
            <Alert status="info" borderRadius="md" mb={4} py={2} fontSize="sm">
              <AlertIcon boxSize={4} />
              Bloqueios permitidos a partir de hoje até 6 meses à frente. Máximo
              de 186 dias entre data inicial e final.
            </Alert>

            <SimpleGrid columns={[1, 1, 2, 4]} spacing={4} mt={2}>
              <Box>
                <Input
                  label="Data inicial de criação"
                  name="rangeStartDate"
                  value={rangeStartDate}
                  onChange={(event) => setRangeStartDate(event.target.value)}
                  isRequired
                  type="date"
                  min={todayStr}
                  max={maxDateStr}
                />
              </Box>

              <Box>
                <Input
                  label="Data final de criação"
                  name="rangeFinishDate"
                  value={rangeFinishDate}
                  onChange={(event) => setRangeFinishDate(event.target.value)}
                  isRequired
                  type="date"
                  min={rangeStartDate || todayStr}
                  max={maxDateStr}
                />
              </Box>

              <Box>
                <TextFieldControlled
                  label="Horário inicial"
                  name="rangeStartHour"
                  value={rangeStartHour}
                  onChange={(event) => setRangeStartHour(event.target.value)}
                  isRequired
                  type="time"
                />
              </Box>

              <Box>
                <Input
                  label="Horário final"
                  name="rangeFinishHour"
                  value={rangeFinishHour}
                  onChange={(event) => setRangeFinishHour(event.target.value)}
                  isRequired
                  type="time"
                />
              </Box>
            </SimpleGrid>

            <Box mt={4}>
              <SelectComponent
                options={optionsDays}
                isMulti
                name="rangeDays"
                value={weeksDay}
                onChange={(e: SelectTypeWeek[]) => setWeeksDay(e || [])}
                placeholder={t('create.container1.placeholder.days')}
                title="Dias em que será criado"
              />
            </Box>

            {/* Range preview */}
            <Flex
              mt={4}
              p={3}
              bg={rangePreview > 0 ? 'orange.50' : 'gray.50'}
              border="1px solid"
              borderColor={rangePreview > 0 ? 'orange.200' : 'gray.200'}
              borderRadius="md"
              alignItems="center"
              gap={2}
            >
              <FiInfo size={14} color={rangePreview > 0 ? '#DD6B20' : '#718096'} />
              <Text
                fontSize="sm"
                color={rangePreview > 0 ? 'orange.600' : 'gray.500'}
                fontWeight={rangePreview > 0 ? 'medium' : 'normal'}
              >
                {rangePreview > 0
                  ? `${rangePreview} bloqueio(s) serão criados`
                  : 'Selecione datas e dias para pré-visualizar'}
              </Text>
            </Flex>
          </CardContainer>
        )}

        {/* Card 3 — Especialistas e descrição */}
        <CardContainer title="Especialistas e Observações">
          <Stack spacing={4} mt={2}>
            <Box>
              <SelectComponent
                title={t('create.container1.i5')}
                options={service?.specialistsEdit?.map((user) => ({
                  value: user?.user?.id,
                  label: user?.social_name
                }))}
                isRequired
                placeholder={t('create.container1.placeholder.specialist')}
                isMulti
                isClearable
                value={specialist}
                onChange={(event: any) => setSpecialist(event || [])}
              />
            </Box>

            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                {t('create.container1.i4')}
              </Text>
              <Textarea
                {...register('description')}
                placeholder={t('create.container1.placeholder.description')}
                defaultValue={state?.description || ''}
                borderRadius="md"
                resize="vertical"
                size="sm"
              />
            </Box>
          </Stack>
        </CardContainer>

        {/* Submit */}
        <Flex w="100%" justifyContent="flex-end" pb={2}>
          <Button
            type="submit"
            size="md"
            colorScheme="blue"
            isLoading={formState.isSubmitting}
            px={8}
          >
            {selectedMode === 'range' ? 'Criar períodos' : t('create.button')}
          </Button>
        </Flex>

        <Box h="200px" />
      </Stack>
    </LayoutDefault>
  )
}
